import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, ChevronDown, ChevronRight, CircleCheck, Clock, History, UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useCan } from '@/hooks/useCan'
import { useMe } from '@/features/auth/hooks/useAuth'
import { formatDateTime } from '@/lib/dateUtils'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import {
    MIN_REASON_LENGTH, scanStatusChangeView, type ScanStatusChange,
} from '@/lib/utils/scan-status-change'
import { scanStatusApi } from '../scan-status-api'

/**
 * A person changing this version's status, on the scan report.
 *
 * Andrew, 2026-10-05: after investigating, a user who finds the indicators or findings are not
 * valid can change the status to green. It takes two people - one to request, one to approve -
 * "otherwise a single user could hide issues", and it is logged. And on 2026-10-06: not only to
 * green - "from green to Amber or red or from Amber to red or red to Amber. Allow those."
 *
 * What this panel must never do is make the scan's own result disappear. It sits ABOVE the status
 * card and says who changed the status, who approved it and why; the card and every finding
 * beneath it stay exactly as the scanner reported them. A green that a person decided reads as a
 * person's decision, not as the scanner's.
 *
 * Rules and their reasons: backend/src/lib/scan-status-change.js. The server enforces them; this
 * only avoids offering a control it would refuse (lib/utils/scan-status-change.ts).
 */

type Action = 'request' | 'approve' | 'reject' | 'withdraw' | 'revoke'

/** Which actions need words, and how many. */
const NEEDS_REASON: Record<Action, 'required' | 'optional' | 'none'> = {
    request: 'required', approve: 'optional', reject: 'required', withdraw: 'none', revoke: 'required',
}

interface Props {
    versionId: number | null | undefined
    /** The version's effective status. */
    status: number | null | undefined
    analyticsSource: string | null | undefined
    changes: ScanStatusChange[] | undefined
    /** What the scan itself concluded, in the report's own words ("Indicators only"). */
    scanLabel: string
    /** The latest scan left a reference a person can accept: offer the tick box on a request. */
    referenceToAccept?: boolean
}

export function ScanStatusChangePanel({ versionId, status, analyticsSource, changes, scanLabel, referenceToAccept = false }: Props) {
    const { t, i18n } = useTranslation()
    const queryClient = useQueryClient()
    const { data: me } = useMe()
    const mayRequest = useCan('scan-status.request')
    const mayApprove = useCan('scan-status.approve')
    const [action, setAction] = useState<Action | null>(null)
    const [text, setText] = useState('')
    // The status asked for: red 2, amber 3, green 4.
    const [target, setTarget] = useState<number | null>(null)
    const [historyOpen, setHistoryOpen] = useState(false)
    // "Also accept this scan as the new reference": its own decision, never ticked for the person.
    const [acceptReference, setAcceptReference] = useState(false)

    const view = scanStatusChangeView({ status, analyticsSource, changes, myUserId: me?.id, mayRequest, mayApprove })
    const subject = view.pending || view.inEffect

    const mutation = useMutation({
        mutationFn: async (a: Action) => {
            const words = text.trim()
            if (a === 'request') return scanStatusApi.request(versionId as number, target as number, words, referenceToAccept && acceptReference)
            if (!subject) throw new Error('No status change to act on')
            if (a === 'approve') return scanStatusApi.approve(subject.id, words || undefined)
            if (a === 'revoke') return scanStatusApi.revoke(subject.id, words)
            return scanStatusApi.reject(subject.id, words || undefined)
        },
        onSuccess: () => {
            setAction(null)
            setText('')
            // The status is on the versions list, the object, the canvas and the roll-up.
            queryClient.invalidateQueries({ queryKey: ['objects'] })
            // ...and on the Scan status changes screen and its sidebar marker.
            queryClient.invalidateQueries({ queryKey: ['scan-status-changes'] })
        },
    })

    if (versionId == null) return null
    const nothingToShow = !view.inEffect && !view.pending && !view.superseded && !view.canRequest && view.history.length === 0
    if (nothingToShow) return null

    const when = (d: string | null | undefined) => (d ? formatDateTime(d, i18n.language) : '')
    const who = (p: { name: string | null } | null | undefined) => p?.name || t('objects.statusChange.someone')
    const open = (a: Action) => {
        mutation.reset()
        setText('')
        setAcceptReference(false)
        // Green is what is usually asked for when a scan found something; otherwise the person picks.
        setTarget(a === 'request' && view.targets.includes(4) ? 4 : null)
        setAction(a)
    }
    // A colour's name, in words: colour is never the only signal.
    const nameOf = (s: number | null | undefined) => (
        s === 2 ? t('dashboard.status.red') : s === 3 ? t('dashboard.status.amber') : s === 4 ? t('dashboard.status.green') : s === 5 ? t('objects.versions.indicators') : String(s ?? '')
    )
    /** What a change does: from this status to that one, as dots and as words. */
    const fromTo = (c: ScanStatusChange) => (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs font-medium">
            <AnalyticsStatusIndicator status={c.previousStatus} size="h-3.5 w-3.5" />
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            <AnalyticsStatusIndicator status={c.newStatus} size="h-3.5 w-3.5" />
            <span>{t('objects.statusChange.fromTo', { from: nameOf(c.previousStatus), to: nameOf(c.newStatus) })}</span>
        </div>
    )

    /** What became of "accept as the new reference" on a change, in words. */
    const referenceLine = (c: ScanStatusChange) => {
        if (!c.acceptReference) return null
        if (c.state === 'pending') return <p className="text-xs font-medium mt-2">{t('objects.statusChange.reference.pending')}</p>
        if (!c.referenceState) return null
        return (
            <p className={`text-xs mt-2 ${c.referenceState === 'failed' ? 'font-medium text-amber-700 dark:text-amber-300' : ''}`} data-testid={`reference-${c.referenceState}`}>
                {t(`objects.statusChange.reference.${c.referenceState}`, { detail: c.referenceDetail || '' })}
            </p>
        )
    }

    // The server answers a refusal with a code; say it in the reader's language, and fall back to
    // the server's own sentence for a code this screen has not met.
    const failure = mutation.error as any
    const failureCode = failure?.response?.data?.code
    const failureText: string | null = failure
        ? String(t(`objects.statusChange.error.${failureCode}`, { defaultValue: failure?.response?.data?.message || failure?.message || t('objects.statusChange.error.generic') }))
        : null

    const need = action ? NEEDS_REASON[action] : 'none'
    const tooShort = need === 'required' && text.trim().length < MIN_REASON_LENGTH
    const noTarget = action === 'request' && target == null

    const form = action && (
        <div className="mt-3 rounded-md border bg-background p-3 space-y-2">
            <div className="text-sm font-semibold">{t(`objects.statusChange.form.${action}Title`)}</div>
            <p className="text-xs text-muted-foreground">{t(`objects.statusChange.form.${action}Help`)}</p>
            {action === 'request' && (
                <div role="radiogroup" aria-label={t('objects.statusChange.form.targetLabel')} className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium">{t('objects.statusChange.form.targetLabel')}</span>
                    {view.targets.map((s) => (
                        <button
                            key={s}
                            type="button"
                            role="radio"
                            aria-checked={target === s}
                            onClick={() => setTarget(s)}
                            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm ${target === s ? 'border-primary bg-primary/10 font-semibold ring-1 ring-primary' : 'hover:bg-muted'}`}
                        >
                            <AnalyticsStatusIndicator status={s} size="h-3.5 w-3.5" />
                            {nameOf(s)}
                        </button>
                    ))}
                </div>
            )}
            {need !== 'none' && (
                <>
                    <Textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={3}
                        maxLength={1000}
                        autoFocus
                        aria-label={t(`objects.statusChange.form.${action}Label`)}
                        placeholder={t(`objects.statusChange.form.${action}Label`)}
                    />
                    {need === 'required' && (
                        <p className="text-[11px] text-muted-foreground">{t('objects.statusChange.form.minimum', { n: MIN_REASON_LENGTH })}</p>
                    )}
                </>
            )}
            {action === 'request' && referenceToAccept && (
                <label className="flex items-start gap-2.5 rounded-md border p-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 flex-shrink-0"
                        checked={acceptReference}
                        onChange={(e) => setAcceptReference(e.target.checked)}
                    />
                    <span className="min-w-0">
                        <span className="block text-sm font-medium">{t('objects.statusChange.reference.tick')}</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{t('objects.statusChange.reference.tickHelp')}</span>
                    </span>
                </label>
            )}
            {action === 'approve' && subject?.acceptReference && (
                <p role="status" className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-medium dark:border-amber-800/60 dark:bg-amber-950/30">
                    {t('objects.statusChange.reference.pending')}
                </p>
            )}
            {failureText && <p role="alert" className="text-xs font-medium text-red-600 dark:text-red-400">{failureText}</p>}
            <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAction(null)} disabled={mutation.isPending}>{t('common.cancel')}</Button>
                <Button
                    size="sm"
                    variant={action === 'reject' || action === 'revoke' ? 'destructive' : 'default'}
                    disabled={tooShort || noTarget || mutation.isPending}
                    onClick={() => mutation.mutate(action)}
                >
                    {t(`objects.statusChange.form.${action}Confirm`)}
                </Button>
            </div>
        </div>
    )

    return (
        <div className="mb-4 space-y-2" data-testid="scan-status-change">
            {/* IN EFFECT: the status is green because two people said so. Neutral, not green: the
                colour belongs to the scanner's result; this is a record of a decision. */}
            {view.inEffect && (
                <div className="rounded-lg border border-slate-400 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-900/40">
                    <div className="flex items-start gap-2.5">
                        <UserCheck className="h-5 w-5 flex-shrink-0 mt-0.5 text-slate-700 dark:text-slate-300" />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold">{t('objects.statusChange.inEffectTitle')}</div>
                            {fromTo(view.inEffect)}
                            <p className="text-xs mt-0.5 leading-relaxed">
                                {t('objects.statusChange.inEffectBody', {
                                    scan: scanLabel,
                                    requester: who(view.inEffect.requestedBy), requestedAt: when(view.inEffect.requestedAt),
                                    approver: who(view.inEffect.decidedBy), approvedAt: when(view.inEffect.decidedAt),
                                })}
                            </p>
                            <Quote label={t('objects.statusChange.reason')} text={view.inEffect.reason} />
                            {view.inEffect.decisionNote && <Quote label={t('objects.statusChange.approvalNote')} text={view.inEffect.decisionNote} />}
                            {referenceLine(view.inEffect)}
                            <p className="text-[11px] text-muted-foreground mt-2">{t('objects.statusChange.findingsUnchanged')}</p>
                            {view.canRevoke && !action && (
                                <Button size="sm" variant="outline" className="mt-2 h-8" onClick={() => open('revoke')}>
                                    {t('objects.statusChange.revoke')}
                                </Button>
                            )}
                        </div>
                    </div>
                    {action === 'revoke' && form}
                </div>
            )}

            {/* PENDING: nothing has changed yet, and it says so. */}
            {view.pending && (
                <div className="rounded-lg border border-dashed border-slate-400 bg-background p-3 dark:border-slate-600">
                    <div className="flex items-start gap-2.5">
                        <Clock className="h-5 w-5 flex-shrink-0 mt-0.5 text-slate-600 dark:text-slate-300" />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold">{t('objects.statusChange.pendingTitle')}</div>
                            {fromTo(view.pending)}
                            <p className="text-xs mt-0.5 leading-relaxed">
                                {t('objects.statusChange.pendingBody', { requester: who(view.pending.requestedBy), requestedAt: when(view.pending.requestedAt) })}
                            </p>
                            <Quote label={t('objects.statusChange.reason')} text={view.pending.reason} />
                            {!action && referenceLine(view.pending)}
                            {view.pendingOutOfDate ? (
                                <p role="status" className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-medium dark:border-amber-800/60 dark:bg-amber-950/30">
                                    {t('objects.statusChange.pendingOutOfDate', { status: nameOf(status ?? null) })}
                                </p>
                            ) : (
                                view.isOwnRequest && <p className="text-xs font-medium mt-2">{t('objects.statusChange.ownRequest')}</p>
                            )}
                            {!action && (view.canApprove || view.canReject || view.canWithdraw) && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {view.canApprove && <Button size="sm" className="h-8" onClick={() => open('approve')}>{t('objects.statusChange.approve')}</Button>}
                                    {view.canReject && <Button size="sm" variant="outline" className="h-8" onClick={() => open('reject')}>{t('objects.statusChange.reject')}</Button>}
                                    {view.canWithdraw && <Button size="sm" variant="outline" className="h-8" onClick={() => open('withdraw')}>{t('objects.statusChange.withdraw')}</Button>}
                                </div>
                            )}
                        </div>
                    </div>
                    {(action === 'approve' || action === 'reject' || action === 'withdraw') && form}
                </div>
            )}

            {/* SUPERSEDED: approved once, but the version has been scanned again since. */}
            {view.superseded && !view.pending && (
                <p className="rounded-lg border bg-muted/40 px-3 py-2 text-xs leading-relaxed">
                    {t('objects.statusChange.supersededBody', { approvedAt: when(view.superseded.decidedAt) })}
                </p>
            )}

            {/* REQUEST */}
            {view.canRequest && (
                <div>
                    {!action && (
                        // The one action this report offers on the status, so it reads as one: a filled
                        // button with a tick, not a quiet outline (Andrew, 2026-10-06).
                        <Button className="gap-2" onClick={() => open('request')}>
                            <CircleCheck className="h-4 w-4" />
                            {t('objects.statusChange.request')}
                        </Button>
                    )}
                    {action === 'request' && form}
                </div>
            )}

            {/* THE RECORD: every request, and what became of it. */}
            {view.history.length > 0 && (
                <div>
                    {/* A real button, not a line of small grey text: the history is the record of who
                        changed this version's status and why, and it was being missed (Andrew, 6 Oct). */}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-2"
                        aria-expanded={historyOpen}
                        onClick={() => setHistoryOpen((o) => !o)}
                    >
                        <History className="h-4 w-4" />
                        {t('objects.statusChange.history')}
                        <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">{view.history.length}</span>
                        {historyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                    {historyOpen && (
                        <ol className="mt-2 space-y-2">
                            {view.history.map((c) => (
                                <li key={c.id} className="rounded-md border px-3 py-2 text-xs leading-relaxed">
                                    <div className="font-medium">{t(`objects.statusChange.state.${c.state}`)}</div>
                                    {fromTo(c)}
                                    <div>{t('objects.statusChange.historyRequested', { who: who(c.requestedBy), when: when(c.requestedAt) })}</div>
                                    <div className="text-muted-foreground break-words">{c.reason}</div>
                                    {c.state !== 'pending' && referenceLine(c)}
                                    {c.decidedAt && (
                                        <div className="mt-1">
                                            {t(`objects.statusChange.historyDecided.${c.state === 'revoked' ? 'approved' : c.state}`, { who: who(c.decidedBy), when: when(c.decidedAt) })}
                                            {c.decisionNote && <span className="text-muted-foreground break-words"> — {c.decisionNote}</span>}
                                        </div>
                                    )}
                                    {c.revokedAt && (
                                        <div className="mt-1">
                                            {t('objects.statusChange.historyRevoked', { who: who(c.revokedBy), when: when(c.revokedAt) })}
                                            {c.revokeReason && <span className="text-muted-foreground break-words"> — {c.revokeReason}</span>}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            )}
        </div>
    )
}

function Quote({ label, text }: { label: string; text: string }) {
    return (
        <div className="mt-2 border-l-2 border-slate-300 pl-2.5 dark:border-slate-600">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-xs whitespace-pre-wrap break-words">{text}</div>
        </div>
    )
}
